import React, { type SVGProps } from 'react'
import { Tooltip, Stack } from '@mui/material'

interface FeedbackTypeViewerProps {
  feedbackTypes: string[]
}

const feedbackIconMap: Record<string, { icon: React.ReactElement; bg: string; color: string; label: string }> = {
  suggested_response: {
    icon: <SuggestedResponseIcon />,
    bg: '#FFE4F0',
    color: '#E91E63',
    label: 'Suggested Response',
  },
  grammar_insights: {
    icon: <GrammarInsightsIcon />,
    bg: '#E3F2FD',
    color: '#2196F3',
    label: 'Grammar Insights',
  },
  actfl: {
    icon: <ProficiencyLevelIcon />,
    bg: '#EDE7F6',
    color: '#3F51B5',
    label: 'Proficiency Level',
  },
  wida: {
    icon: <ProficiencyLevelIcon />,
    bg: '#FFF8E1',
    color: '#FFB300',
    label: 'Proficiency Level',
  },
  proficiency_level: {
    icon: <ProficiencyLevelIcon />,
    bg: '#FFF8E1',
    color: '#FFB300',
    label: 'Proficiency Level',
  },
}

function FeedbackTypeViewer({ feedbackTypes }: FeedbackTypeViewerProps): JSX.Element {
  return (
    <Stack direction="row" gap="4px">
      {feedbackTypes.map(type => {
        const config = feedbackIconMap[type]

        if (!config) return null

        return (
          <Tooltip key={type} arrow title={config.label}>
            <span>{config.icon}</span>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

export default FeedbackTypeViewer

function GrammarInsightsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={22} viewBox="0 0 22 22" width={22} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M11 21.5C5.20101 21.5 0.5 16.799 0.5 11C0.5 5.20101 5.20101 0.5 11 0.5C16.799 0.5 21.5 5.20101 21.5 11C21.5 16.799 16.799 21.5 11 21.5Z"
        fill="#FDF2FA"
      />
      <path
        d="M11 21.5C5.20101 21.5 0.5 16.799 0.5 11C0.5 5.20101 5.20101 0.5 11 0.5C16.799 0.5 21.5 5.20101 21.5 11C21.5 16.799 16.799 21.5 11 21.5Z"
        stroke="#FCCEEE"
      />
      <g clipPath="url(#clip0_3442_19216)">
        <path
          d="M11 10.25V8.5M11 12H11.005M9.95 14.6L10.68 15.5733C10.7886 15.7181 10.8428 15.7905 10.9094 15.8163C10.9677 15.839 11.0323 15.839 11.0906 15.8163C11.1572 15.7905 11.2114 15.7181 11.32 15.5733L12.05 14.6C12.1966 14.4046 12.2699 14.3069 12.3593 14.2322C12.4784 14.1328 12.6192 14.0624 12.7702 14.0268C12.8836 14 13.0057 14 13.25 14C13.9489 14 14.2984 14 14.574 13.8858C14.9416 13.7336 15.2336 13.4416 15.3858 13.074C15.5 12.7984 15.5 12.4489 15.5 11.75V8.9C15.5 8.05992 15.5 7.63988 15.3365 7.31901C15.1927 7.03677 14.9632 6.8073 14.681 6.66349C14.3601 6.5 13.9401 6.5 13.1 6.5H8.9C8.05992 6.5 7.63988 6.5 7.31901 6.66349C7.03677 6.8073 6.8073 7.03677 6.66349 7.31901C6.5 7.63988 6.5 8.05992 6.5 8.9V11.75C6.5 12.4489 6.5 12.7984 6.61418 13.074C6.76642 13.4416 7.05843 13.7336 7.42597 13.8858C7.70163 14 8.05109 14 8.75 14C8.99429 14 9.11643 14 9.22975 14.0268C9.38085 14.0624 9.52156 14.1328 9.64075 14.2322C9.73014 14.3069 9.80343 14.4046 9.95 14.6Z"
          stroke="#EE46BC"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
        />
      </g>
      <defs>
        <clipPath id="clip0_3442_19216">
          <rect fill="white" height={12} transform="translate(5 5)" width={12} />
        </clipPath>
      </defs>
    </svg>
  )
}

function ProficiencyLevelIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={22} viewBox="0 0 22 22" width={22} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M11 21.5C5.20101 21.5 0.5 16.799 0.5 11C0.5 5.20101 5.20101 0.5 11 0.5C16.799 0.5 21.5 5.20101 21.5 11C21.5 16.799 16.799 21.5 11 21.5Z"
        fill="#EEF4FF"
      />
      <path
        d="M11 21.5C5.20101 21.5 0.5 16.799 0.5 11C0.5 5.20101 5.20101 0.5 11 0.5C16.799 0.5 21.5 5.20101 21.5 11C21.5 16.799 16.799 21.5 11 21.5Z"
        stroke="#C7D7FE"
      />
      <g clipPath="url(#clip0_3442_19222)">
        <path
          d="M9 11.5V13.5M13 10.5V13.5M11 8.5V13.5M16 11C16 13.7614 13.7614 16 11 16C8.23858 16 6 13.7614 6 11C6 8.23858 8.23858 6 11 6C13.7614 6 16 8.23858 16 11Z"
          stroke="#6172F3"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
        />
      </g>
      <defs>
        <clipPath id="clip0_3442_19222">
          <rect fill="white" height={12} transform="translate(5 5)" width={12} />
        </clipPath>
      </defs>
    </svg>
  )
}
function SuggestedResponseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={22} viewBox="0 0 22 22" width={22} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M11 21.5C5.20101 21.5 0.5 16.799 0.5 11C0.5 5.20101 5.20101 0.5 11 0.5C16.799 0.5 21.5 5.20101 21.5 11C21.5 16.799 16.799 21.5 11 21.5Z"
        fill="#EFF8FF"
      />
      <path
        d="M11 21.5C5.20101 21.5 0.5 16.799 0.5 11C0.5 5.20101 5.20101 0.5 11 0.5C16.799 0.5 21.5 5.20101 21.5 11C21.5 16.799 16.799 21.5 11 21.5Z"
        stroke="#B2DDFF"
      />
      <path
        d="M13 7C13.465 7 13.6975 7 13.8882 7.05111C14.4059 7.18981 14.8102 7.59413 14.9489 8.11177C15 8.30252 15 8.53501 15 9V13.6C15 14.4401 15 14.8601 14.8365 15.181C14.6927 15.4632 14.4632 15.6927 14.181 15.8365C13.8601 16 13.4401 16 12.6 16H9.4C8.55992 16 8.13988 16 7.81901 15.8365C7.53677 15.6927 7.3073 15.4632 7.16349 15.181C7 14.8601 7 14.4401 7 13.6V9C7 8.53501 7 8.30252 7.05111 8.11177C7.18981 7.59413 7.59413 7.18981 8.11177 7.05111C8.30252 7 8.53501 7 9 7M9.5 12.5L10.5 13.5L12.75 11.25M9.8 8H12.2C12.48 8 12.62 8 12.727 7.9455C12.8211 7.89757 12.8976 7.82108 12.9455 7.727C13 7.62004 13 7.48003 13 7.2V6.8C13 6.51997 13 6.37996 12.9455 6.273C12.8976 6.17892 12.8211 6.10243 12.727 6.0545C12.62 6 12.48 6 12.2 6H9.8C9.51997 6 9.37996 6 9.273 6.0545C9.17892 6.10243 9.10243 6.17892 9.0545 6.273C9 6.37996 9 6.51997 9 6.8V7.2C9 7.48003 9 7.62004 9.0545 7.727C9.10243 7.82108 9.17892 7.89757 9.273 7.9455C9.37996 8 9.51997 8 9.8 8Z"
        stroke="#2E90FA"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
      />
    </svg>
  )
}
